import React, { FC } from 'react';

import { UploadFile } from 'antd/lib/upload/interface';

import * as Types from '../types';
import { Task } from '../types/task';

import Preview from '../preview';
import AttachFile from '../attach_file';
import CreateTaskRichTextAdapter from '../../../../components/RichTextDescription/CreateTaskAdapter';
import OriginalTextFromSelection from '../../../../components/OriginalTextFromSelection';
import { resolveRichTextTaskFieldShortcutPermissions } from '../isClientUserForTaskShortcuts';

import cx from 'classnames';
import styles from './index.module.css';

type Props = {
  setUploadingState: (state: boolean) => void;
  container: HTMLDivElement;
  task: Task;
  accessTo: Types.AccessTo;
  modalContainer: HTMLDivElement;
  modalOuter: HTMLDivElement;
  attachmentsUrl: string;
  removeFile: (file: UploadFile<any>) => void;
  fileList: any[];
  onFileListChange: (fileList: any[]) => void;
  assignees: number[];
  onAssigneesChange: (assignees: number[]) => void;
  watchers: number[];
  onWatchersChange: (watchers: number[]) => void;
  guestVisible: boolean;
  description: string;
  onDescriptionChange: (description: string) => void;
  submitTask: () => any;
  severity: Types.Severity;
  setOverlay: (overlay: Types.Overlay) => void;
  onAnnotationChange: (dataURI: Blob) => void;
  dataURI?: Blob | null;
  isAdminView: boolean;
  assignableUsers?: Types.User[];
  subscriptionUrl: string;
  owner: {
    id: number;
    email?: string;
  };
  availableTags: Types.Tag[];
  onTagsChange: (tags: Types.Tag[]) => void;
  tags: Types.Tag[];
  createTagsViaNewTask: boolean;
  handleUpdateUserName: () => void;
  onSeverityChange: (severity: Types.Severity) => void;
  status?: number;
  onStatusChange: (status: number) => void;
  columns: Types.Column[];
  apiDomain: string;
  projectId: number;
  pastedFile: File;
  mentionableTasks: Types.MentionableTask[];
  currentUserId?: number;
  isClientUser: boolean;
  dueAt: string | null;
  onDueDateChange: (dueAt: string | null) => void;
  canEditDueDate: boolean;
};

const LeftPanel: FC<Props> = props => {
  const {
    setUploadingState,
    container,
    task,
    accessTo,
    modalContainer,
    modalOuter,
    attachmentsUrl,
    removeFile,
    fileList,
    onFileListChange,
    onAssigneesChange,
    watchers,
    onWatchersChange,
    guestVisible,
    description,
    onDescriptionChange,
    submitTask,
    assignees,
    severity,
    onSeverityChange,
    status,
    onStatusChange,
    columns,
    tags,
    onTagsChange,
    availableTags,
    setOverlay,
    onAnnotationChange,
    dataURI,
    isAdminView,
    assignableUsers,
    subscriptionUrl,
    owner,
    createTagsViaNewTask,
    apiDomain,
    projectId,
    pastedFile,
    mentionableTasks,
    currentUserId,
    isClientUser,
    dueAt,
    onDueDateChange,
    canEditDueDate
  } = props;

  const showScreenshotPreview = !isAdminView;
  const {
    canEditAssignees,
    canEditSeverity,
    canEditStatus,
    canEditTags
  } = accessTo;
  const richTextShortcutPermissions = resolveRichTextTaskFieldShortcutPermissions(
    {
      canEditAssignees,
      canEditSeverity,
      canEditStatus,
      canEditTags,
      canEditDueDate
    },
    isClientUser
  );

  return (
    <div className={cx(styles.leftPanel, styles.richTextLeftPanel)}>
      <OriginalTextFromSelection
        selectedText={task.selectedText}
        className={styles.fromSelection}
        isCreateTask
      />
      <div
        className={cx({
          [styles.descriptionOuter]: true,
          [styles.shortDescriptionOuter]: !!task.screenshotPolyfill
        })}
      >
        <CreateTaskRichTextAdapter
          submitTask={submitTask}
          description={description}
          onDescriptionChange={onDescriptionChange}
          fullHeight={isAdminView || !!task.screenshotPolyfill}
          assignees={assignees}
          onAssigneesChange={onAssigneesChange}
          watchers={watchers}
          onWatchersChange={onWatchersChange}
          guestVisible={!!guestVisible}
          canEditAssignees={richTextShortcutPermissions.canEditAssignees}
          assignableUsers={assignableUsers || []}
          severity={severity}
          onSeverityChange={onSeverityChange}
          canEditSeverity={richTextShortcutPermissions.canEditSeverity}
          status={status}
          onStatusChange={onStatusChange}
          columns={columns}
          canEditStatus={richTextShortcutPermissions.canEditStatus}
          tags={tags}
          onTagsChange={onTagsChange}
          availableTags={availableTags}
          canEditTags={richTextShortcutPermissions.canEditTags}
          createTagsViaNewTask={createTagsViaNewTask}
          mentionableTasks={mentionableTasks}
          currentUserId={currentUserId}
          dueAt={dueAt}
          onDueDateChange={onDueDateChange}
          canEditDueDate={!!richTextShortcutPermissions.canEditDueDate}
          descriptionPlaceholder={
            task.selectedText ? `"${task.selectedText}"` : undefined
          }
          emojiPickerConfig={{
            getContainer: () => container,
            apiDomain,
            isAdminView,
            projectId
          }}
        />
        <div
          className={cx(styles.descriptionFooter, styles.richTextFooter)}
        >
          <div className={styles.footerRight}>
            <AttachFile
              {...{
                endpoint: attachmentsUrl,
                setUploadingState,
                fileList,
                onFileListChange,
                removeFile,
                modalContainer,
                modalOuter,
                pastedFile
              }}
            />
          </div>
        </div>
      </div>
      {showScreenshotPreview && task && (
        <Preview
          {...{
            task,
            dataURI,
            container,
            accessTo,
            severity,
            setOverlay,
            owner,
            subscriptionUrl,
            onAnnotationChange
          }}
        />
      )}
    </div>
  );
};

export default LeftPanel;
